import { DeviceSpecificEqualizerContour, MicOptimizationMode } from '@shure/shared/angular/data-access/system-api/core';
import {
	getEnabledFlagForEqualizerFiltersFromSysApi,
	isParametricTypeValid,
	mapChannelGroupFromSysApi,
	mapDeviceEqualizerContourFromSysApi,
	mapMicOptimizationModeFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { EqFilterType } from '@shure/shared/angular/data-access/system-api/models';

import { EqualizerFilter, ParametricEqualizerChannel } from '../../api';
import {
	EqualizerFragment,
	ParametricEqualizerChannelFragment
} from '../graphql/generated/parametric-equalizer.generated';

export function mapPeqChannelFromSysApi(channel: ParametricEqualizerChannelFragment): ParametricEqualizerChannel {
	const { id, description, features } = channel;
	return {
		id,
		enabled: getEnabledFlagForEqualizerFiltersFromSysApi(channel.features.equalizer?.filters),
		name: features.name?.name,
		isParametricTypeValid: isParametricTypeValid(channel.features.equalizer?.filters || null),
		group: mapChannelGroupFromSysApi(channel.description.groupName),
		description: {
			constraints: {
				deviceSpecificEqualizerContour: {
					options:
						description.constraints.deviceSpecificEqualizerContour?.deviceSpecificEqualizerContour?.options?.map(
							(option) => mapDeviceEqualizerContourFromSysApi(option)
						) || []
				}
			},
			features: {
				equalizerFilters: { supported: description.features.equalizer.supported },
				micOptimization: { supported: description.features.micOptimization.supported },
				deviceEqualizerContour: {
					supported: description.features.deviceSpecificEqualizerContour.supported
				}
			}
		},
		features: {
			equalizerFilters: mapEqualizerFiltersFromSysApi(channel),
			micOptimization: {
				mode: mapMicOptimizationModeFromSysApi(features.micOptimization?.mode || MicOptimizationMode.Normal),
				isMissing: !description.features.micOptimization.supported
			},
			deviceEqualizerContour: {
				deviceSpecificEqualizerContour: mapDeviceEqualizerContourFromSysApi(
					features.deviceSpecificEqualizerContour?.deviceSpecificEqualizerContour ??
						DeviceSpecificEqualizerContour.Off
				),
				isMissing: !description.features.deviceSpecificEqualizerContour.supported
			},
			channelConfiguration: channel.description.features.channelConfiguration.supported
				? {
						enabled: channel.features.channelConfiguration?.enabled || false,
						isMissing: channel.features.channelConfiguration === null
				  }
				: undefined
		}
	};
}

function mapEqualizerFiltersFromSysApi(channel: ParametricEqualizerChannelFragment): EqualizerFilter[] {
	const {
		description,
		features: { equalizer }
	} = channel;

	if (!description.features.equalizer.supported) {
		return [];
	}

	return equalizer ? equalizer.filters.map((f) => mapEqualizerFilterFromSysApi(f)) : [];
}

export function mapEqualizerFilterFromSysApi(filter: EqualizerFragment): EqualizerFilter {
	const { id, features, description } = filter;
	const type = features.configuration?.filterType ?? EqFilterType.Parametric;
	let gain = features.gain?.gain ?? 0;

	if (type === EqFilterType.LowCut || type === EqFilterType.HighCut) {
		gain = 0; // low cut and high cut filters have a fixed gain of 0 dB
	}

	return {
		id,
		description: {
			constraints: {
				validFilterTypes: description.constraints.configuration?.filterType?.options || [],
				gain: {
					step: description.constraints.gain?.gain.range.step ?? 0.1
				}
			}
		},
		features: {
			enabled: features.configuration?.enabled || false,
			type,
			frequency: features.frequency?.frequency || 20,
			gain,
			qFactor: features.qFactor?.qFactor || 0
		}
	};
}
